import { useState, useEffect, useCallback, useRef } from "react"
import { useParams } from "react-router-dom"
import { useOrganization } from "@/contexts/organization-context"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Textarea } from "@/components/ui/textarea"
import { YamlEditor, findLineNumbersForValues } from "@/components/ui/yaml-editor"
import { Switch } from "@/components/ui/switch"
import { toast } from "sonner"
import {
  Plus,
  Trash2
} from "lucide-react"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  NormalizationType,
  NormalizationTypeOption
} from "@/types/normalization"
import { normalizationsService } from "@/services/normalizations"
import { integratorsService } from "@/services/integrators"
import { integrationsService, IntegrationType, IntegratorType } from "@/services/integrations"
import { api } from "@/services/api"
import {
  extractIntegrationReferences,
  validateIntegrationReferences,
  IntegrationValidationError
} from "@/utils/integration-conditional-parser"

interface PrimitiveData {
  id?: number
  name: string
  slug?: string
  description?: string
  type?: string
}

// Mock data for development - replace with actual API calls
const mockNormalizations: NormalizationType[] = [
  {
    unique_serial_id: 1,
    organization_id: 1,
    kpi: "customer_name",
    normalization: {
      rules: [
        { type: "case", config: { transform: "title" } },
        { type: "replace", config: { from: "Inc.", to: "Incorporated" } }
      ]
    },
    tags: "customer,name,formatting",
    created_at: "2024-01-15T10:30:00Z",
    updated_at: "2024-01-15T10:30:00Z"
  },
  {
    unique_serial_id: 2,
    organization_id: 1,
    kpi: "country_code",
    normalization: {
      rules: [
        { type: "countries", config: { format: "iso2" } }
      ]
    },
    tags: "location,country,iso",
    created_at: "2024-01-16T14:20:00Z",
    updated_at: "2024-01-16T14:20:00Z"
  }
]

const normalizationTypes: NormalizationTypeOption[] = [
  { value: "currency", label: "Currency", description: "Currency conversion" },
  { value: "replace", label: "Replace", description: "Exact text replacements" },
  { value: "partial", label: "Partial String", description: "Substring replacements" },
  { value: "conditional", label: "Conditional", description: "Rule-based logic" },
  { value: "case", label: "Case Transform", description: "Change text case" },
  { value: "countries", label: "Countries", description: "Country name standardization" },
  { value: "fuzzy", label: "Fuzzy Matching", description: "Approximate string matching" }
]

export function NormalizationsPage() {
  const { } = useParams<{ orgSlug: string }>()
  const { currentOrganization } = useOrganization()
  
  const [normalizations, setNormalizations] = useState<NormalizationType[]>(mockNormalizations)
  const [loading, setLoading] = useState(false)
  const [showNewDialog, setShowNewDialog] = useState(false)
  const [_showEditDialog, setShowEditDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [_hasValidationError, setHasValidationError] = useState(false)
  const [selectedNormalization, setSelectedNormalization] = useState<NormalizationType | null>(null)
  const [_isAddingRuleToExistingKPI, setIsAddingRuleToExistingKPI] = useState(false)
  const [availableKPIs, setAvailableKPIs] = useState<PrimitiveData[]>([])
  const [loadingKPIs, setLoadingKPIs] = useState(false)
  
  // Individual rule editing state
  const [showRuleEditDialog, setShowRuleEditDialog] = useState(false)
  const [editingRule, setEditingRule] = useState<{normalization: NormalizationType, ruleType: string, ruleConfig: any, ruleIndex: number} | null>(null)
  const [editingRuleYAML, setEditingRuleYAML] = useState("")
  const [showRuleDeleteDialog, setShowRuleDeleteDialog] = useState(false)
  const [deletingRule, setDeletingRule] = useState<{normalization: NormalizationType, ruleType: string, ruleIndex: number} | null>(null)
  const [deleteConfirmation, setDeleteConfirmation] = useState("")

  // Inline YAML editing state
  const [editingKpiId, setEditingKpiId] = useState<number | null>(null)
  const [inlineYaml, setInlineYaml] = useState<string>("")
  const [inlineYamlError, setInlineYamlError] = useState<string | null>(null)
  const [savingInlineYaml, setSavingInlineYaml] = useState(false)
  const [errorLineNumbers, setErrorLineNumbers] = useState<number[]>([])  // For CodeMirror highlighting
  const [showSaveConfirmDialog, setShowSaveConfirmDialog] = useState(false)
  const [pendingSaveNormalization, setPendingSaveNormalization] = useState<NormalizationType | null>(null)

  // Integration conditional validation state
  const [validIntegrators, setValidIntegrators] = useState<Set<string>>(new Set())
  const [validIntegrations, setValidIntegrations] = useState<Map<number, string>>(new Map())
  const [integrationValidationErrors, setIntegrationValidationErrors] = useState<IntegrationValidationError[]>([])
  const [loadingValidationData, setLoadingValidationData] = useState(false)

  // Form state
  const [kpi, setKpi] = useState("")
  const [tags, setTags] = useState("")
  const [aiPrompt, setAiPrompt] = useState("")
  const [manualConfig, setManualConfig] = useState("")
  const [useTokenBuilder, setUseTokenBuilder] = useState(true)
  const [tokenInput, setTokenInput] = useState("")
  const [ruleTokens, setRuleTokens] = useState<any[]>([])
  const [_showSuggestions, setShowSuggestions] = useState(false)
  const [_suggestions, setSuggestions] = useState<string[]>([])

  const loadNormalizations = useCallback(async () => {
    setLoading(true)
    try {
      console.log('🔍 Loading normalizations for org:', currentOrganization?.slug)
      const response = await normalizationsService.list()
      console.log('📡 Normalizations API response:', response)
      console.log('📋 Response type:', typeof response)
      console.log('📋 Response keys:', Object.keys(response || {}))
      console.log('📋 Normalizations array:', response?.normalizations)
      console.log('📋 Normalizations count:', response?.normalizations?.length || 0)
      
      if (response?.normalizations) {
        setNormalizations(response.normalizations)
        console.log('✅ Set normalizations:', response.normalizations.length, 'items')
      } else {
        console.log('⚠️ No normalizations property in response')
        setNormalizations([])
      }
    } catch (error) {
      console.error("❌ Failed to load normalizations:", error)
      console.error("❌ Error response:", (error as any)?.response?.data)
      console.error("❌ Error status:", (error as any)?.response?.status)
      
      // Check for subscription-related errors
      const errorResponse = (error as any)?.response?.data
      const errorStatus = (error as any)?.response?.status
      
      if (errorStatus === 403 && errorResponse?.error === 'AUTHORIZATION_ERROR') {
        if (errorResponse.message?.includes('subscription has expired') || 
            errorResponse.message?.includes('subscription is invalid') ||
            errorResponse.message?.includes('subscription is inactive')) {
          // Redirect to billing settings for subscription issues
          const orgSlug = currentOrganization?.slug
          if (orgSlug) {
            window.location.href = `/${orgSlug}/settings#billing`
            return
          }
        }
      }
      
      toast.error("Failed to load normalizations")
      // Fallback to mock data for development
      setNormalizations(mockNormalizations)
    } finally {
      setLoading(false)
    }
  }, [currentOrganization])

  const loadKPIs = useCallback(async () => {
    setLoadingKPIs(true)
    try {
      console.log('🔍 Loading KPIs for org:', currentOrganization?.slug)
      const response = await api.get('/data/primitives')
      const primitives = response.data.primitives || []

      // Combine all primitives (metrics and dimensions) for KPI selection
      const allKPIs = primitives.map((p: PrimitiveData) => ({
        id: p.id,
        name: p.name,
        slug: p.slug || p.name,
        description: p.description,
        type: p.type
      }))

      setAvailableKPIs(allKPIs)
      console.log('✅ Loaded KPIs:', allKPIs.length, 'items')
    } catch (error) {
      console.error("❌ Failed to load KPIs:", error)
      toast.error("Failed to load available KPIs")
      setAvailableKPIs([])
    } finally {
      setLoadingKPIs(false)
    }
  }, [currentOrganization])

  // Load integrators and integrations for validation of integration conditionals
  const loadValidationData = useCallback(async () => {
    setLoadingValidationData(true)
    try {
      console.log('🔍 Loading validation data for integration conditionals')

      // Fetch integrators first
      const integratorsData = await integratorsService.list()

      // Build map of integrator_id -> slug
      const integratorIdToSlug = new Map<number, string>()
      const slugs = new Set<string>()
      integratorsData.forEach((integrator: IntegratorType) => {
        if (integrator.slug) {
          slugs.add(integrator.slug)
          if (integrator.id) {
            integratorIdToSlug.set(integrator.id, integrator.slug)
          }
        }
      })
      setValidIntegrators(slugs)

      // Fetch integrations - try larger page size first, fallback to smaller if needed
      const integrationsMap = new Map<number, string>()

      const fetchIntegrationsPage = async (pageNum: number, size: number) => {
        const response = await integrationsService.list(pageNum, size)
        return response
      }

      // Try with page size 100 first
      let pageSize = 100
      let firstResponse
      try {
        firstResponse = await fetchIntegrationsPage(1, pageSize)
      } catch (e) {
        // Fallback to smaller page size if 100 fails
        console.log('⚠️ Page size 100 failed, trying 30')
        pageSize = 30
        firstResponse = await fetchIntegrationsPage(1, pageSize)
      }

      // Process first page
      firstResponse.integrations.forEach((integration: IntegrationType) => {
        if (integration.id) {
          const slug = integration.integrator?.slug ||
            (integration.integrator_id ? integratorIdToSlug.get(integration.integrator_id) : undefined)
          if (slug) {
            integrationsMap.set(integration.id, slug)
          }
        }
      })

      // Calculate remaining pages and fetch in parallel
      const totalItems = firstResponse.total || firstResponse.integrations.length
      const totalPages = Math.ceil(totalItems / pageSize)

      if (totalPages > 1) {
        const pagePromises = []
        for (let page = 2; page <= Math.min(totalPages, 20); page++) {
          pagePromises.push(fetchIntegrationsPage(page, pageSize))
        }

        const remainingPages = await Promise.all(pagePromises)
        remainingPages.forEach(response => {
          response.integrations.forEach((integration: IntegrationType) => {
            if (integration.id) {
              const slug = integration.integrator?.slug ||
                (integration.integrator_id ? integratorIdToSlug.get(integration.integrator_id) : undefined)
              if (slug) {
                integrationsMap.set(integration.id, slug)
              }
            }
          })
        })
      }

      setValidIntegrations(integrationsMap)

      console.log('✅ Loaded validation data:', slugs.size, 'integrators,', integrationsMap.size, 'integrations')
    } catch (error) {
      console.error("❌ Failed to load validation data:", error)
      toast.error("Failed to load integration validation data - validation will be limited")
    } finally {
      setLoadingValidationData(false)
    }
  }, [])

  // Track if we're already loading to prevent concurrent loads from Strict Mode double-invocation
  const isLoadingRef = useRef(false)
  const loadedOrgRef = useRef<string | null>(null)

  useEffect(() => {
    if (!currentOrganization) return

    const orgSlug = currentOrganization.slug

    // Skip if already loading for this organization
    if (isLoadingRef.current && loadedOrgRef.current === orgSlug) {
      console.log('⏭️ Skipping duplicate load for org:', orgSlug)
      return
    }

    // Mark as loading
    isLoadingRef.current = true
    loadedOrgRef.current = orgSlug

    console.log('🔍 Loading data for org:', orgSlug)

    Promise.all([loadNormalizations(), loadKPIs(), loadValidationData()])
      .finally(() => {
        isLoadingRef.current = false
      })
  }, [currentOrganization, loadNormalizations, loadKPIs, loadValidationData])

  const handleCreateNormalization = async () => {
    if (!kpi.trim()) {
      toast.error("Please select a KPI")
      return
    }

    try {
      // Create normalization with empty config
      const newNormalization = await normalizationsService.create({
        kpi: kpi.trim(),
        normalization: {}, // Empty config - user will edit inline
        tags: undefined
      })

      setNormalizations([...normalizations, newNormalization])
      setShowNewDialog(false)
      resetForm()

      // Automatically open inline editor for the new row
      setTimeout(() => {
        enterInlineEditMode(newNormalization)
      }, 100)

      toast.success("KPI added! Click the rules field to add normalization rules")
    } catch (error: any) {
      console.error("Failed to create normalization:", error)
      toast.error("Failed to create normalization")
    }
  }

  const _handleEditNormalization = async () => {
    if (!selectedNormalization || !kpi.trim()) {
      toast.error("Please enter a KPI name")
      return
    }

    try {
      // Validate YAML syntax before proceeding
      const normalizationConfig = buildNormalizationConfig()

      // Extract dependencies from conditional normalizations
      const dependencies = extractConditionalDependencies(normalizationConfig)
      console.log('🔍 Extracted dependencies:', dependencies)
      console.log('📋 Normalization config:', normalizationConfig)

      const updatedNormalization = await normalizationsService.update(
        selectedNormalization.unique_serial_id!,
        {
          kpi: kpi.trim(),
          normalization: normalizationConfig,
          dependencies: dependencies.length > 0 ? dependencies : undefined,
          tags: tags.trim() || undefined
        }
      )

      setNormalizations(normalizations.map(n => 
        n.unique_serial_id === selectedNormalization.unique_serial_id 
          ? updatedNormalization 
          : n
      ))
      setShowEditDialog(false)
      resetForm()
      toast.success("Normalization updated successfully")
    } catch (error: any) {
      console.error("Failed to update normalization:", error)
      if (error.message === 'Invalid YAML syntax') {
        toast.error("Bad YAML syntax")
        setHasValidationError(true)
        // Keep dialog open so user can fix the syntax
        return
      }
      toast.error("Failed to update normalization")
    }
  }

  const handleDeleteNormalization = async () => {
    if (!selectedNormalization) return

    try {
      await normalizationsService.delete(selectedNormalization.unique_serial_id!)
      
      setNormalizations(normalizations.filter(n => 
        n.unique_serial_id !== selectedNormalization.unique_serial_id
      ))
      setShowDeleteDialog(false)
      setSelectedNormalization(null)
      setDeleteConfirmation("")
      toast.success("Normalization deleted successfully")
    } catch (error) {
      console.error("Failed to delete normalization:", error)
      toast.error("Failed to delete normalization")
    }
  }

  const _handleGenerateWithAI = async () => {
    if (!aiPrompt.trim()) {
      toast.error("Please enter a prompt for AI generation")
      return
    }

    if (!kpi.trim()) {
      toast.error("Please enter a KPI name first")
      return
    }

    try {
      const response = await normalizationsService.generateWithAI({
        kpi: kpi.trim(),
        prompt: aiPrompt.trim()
      })

      setManualConfig(JSON.stringify(response.normalization, null, 2))
      toast.success(`AI generated normalization with ${response.confidence}% confidence`)
      
      if (response.explanation) {
        console.log("AI Explanation:", response.explanation)
      }
    } catch (error) {
      console.error("Failed to generate with AI:", error)
      toast.error("Failed to generate normalization with AI")
      // Fallback message for development
      toast.info("AI generation feature requires backend API")
    }
  }

  const resetForm = () => {
    setKpi("")
    setTags("")
    setAiPrompt("")
    setManualConfig("")
    setSelectedNormalization(null)
    setIsAddingRuleToExistingKPI(false)
    setHasValidationError(false)
    setRuleTokens([])
    setTokenInput("")
    setUseTokenBuilder(true)
    setSuggestions([])
    setShowSuggestions(false)
  }

  const openEditDialog = (normalization: NormalizationType) => {
    setSelectedNormalization(normalization)
    setKpi(normalization.kpi)
    setTags(normalization.tags || "")
    
    // Convert existing normalization to YAML format for editing
    const yamlConfig = convertConfigToYAML(normalization.normalization)
    setManualConfig(yamlConfig)
    
    // Convert to tokens for smart builder
    const tokens = yamlToTokens(yamlConfig)
    setRuleTokens(tokens)
    
    setIsAddingRuleToExistingKPI(false)
    setShowEditDialog(true)
  }

  const openDeleteDialog = (normalization: NormalizationType) => {
    setSelectedNormalization(normalization)
    setDeleteConfirmation("")
    setShowDeleteDialog(true)
  }

  // Individual rule handlers
  const _openRuleEditDialog = (normalization: NormalizationType, ruleType: string, ruleConfig: any, ruleIndex: number) => {
    setEditingRule({ normalization, ruleType, ruleConfig, ruleIndex })
    setEditingRuleYAML(formatRuleDetailsAsYAML(ruleType, ruleConfig))
    setShowRuleEditDialog(true)
  }

  const _openRuleDeleteDialog = (normalization: NormalizationType, ruleType: string, ruleIndex: number) => {
    setDeletingRule({ normalization, ruleType, ruleIndex })
    setShowRuleDeleteDialog(true)
  }

  const _duplicateRule = (normalization: NormalizationType, ruleType: string, ruleConfig: any) => {
    // TODO: Implement duplicate rule functionality
    console.log('Duplicate rule:', { normalization: normalization.kpi, ruleType, ruleConfig })
    toast.info('Duplicate functionality coming soon')
  }

  // Inline YAML editing handlers
  const enterInlineEditMode = (normalization: NormalizationType) => {
    setEditingKpiId(normalization.unique_serial_id!)
    setInlineYaml(convertConfigToYAML(normalization.normalization))
    setInlineYamlError(null)
    setIntegrationValidationErrors([])
    setErrorLineNumbers([])
  }

  const cancelInlineEdit = () => {
    setEditingKpiId(null)
    setInlineYaml("")
    setInlineYamlError(null)
    setIntegrationValidationErrors([])
    setErrorLineNumbers([])
  }

  // Validate YAML and show confirmation dialog
  const validateAndConfirmSave = (normalization: NormalizationType) => {
    if (!editingKpiId) return

    setInlineYamlError(null)
    setIntegrationValidationErrors([])
    setErrorLineNumbers([])

    try {
      // Parse YAML to validate syntax
      const parsedConfig = parseYAMLConfig(inlineYaml)

      // Validate integration conditionals (e.g., integration = 'google_ads#123')
      const integrationErrors = validateIntegrationConditionalsInConfig(parsedConfig)
      if (integrationErrors.length > 0) {
        // Deduplicate errors by value
        const uniqueErrors = integrationErrors.filter((err, idx, arr) =>
          arr.findIndex(e => e.value === err.value) === idx
        )
        setIntegrationValidationErrors(uniqueErrors)

        // Find line numbers for error values to highlight in editor
        const errorValues = uniqueErrors.map(e => e.value)
        const lineMap = findLineNumbersForValues(inlineYaml, errorValues)
        const lines = Array.from(lineMap.values())
        setErrorLineNumbers(lines)
        return // Stop - don't show confirmation dialog
      }

      // Validation passed - show confirmation dialog
      setPendingSaveNormalization(normalization)
      setShowSaveConfirmDialog(true)
    } catch (error: any) {
      console.error('Failed to validate YAML:', error)
      setInlineYamlError(error.message || 'Invalid YAML syntax')
    }
  }

  const saveInlineYaml = async () => {
    if (!editingKpiId || !pendingSaveNormalization) return

    try {
      setSavingInlineYaml(true)
      setShowSaveConfirmDialog(false)

      // Parse YAML to convert to JSON (already validated)
      const parsedConfig = parseYAMLConfig(inlineYaml)

      // Extract dependencies from conditional normalizations
      const dependencies = extractConditionalDependencies(parsedConfig)
      console.log('🔍 Inline edit - Extracted dependencies:', dependencies)
      console.log('📋 Inline edit - Normalization config:', parsedConfig)

      // Prepare update payload
      const updatePayload = {
        kpi: pendingSaveNormalization.kpi,
        normalization: parsedConfig,
        dependencies: dependencies.length > 0 ? dependencies : undefined,
        tags: pendingSaveNormalization.tags
      }
      console.log('📤 Inline edit - Sending update payload:', updatePayload)

      // Save to database
      const updatedNormalization = await normalizationsService.update(editingKpiId, updatePayload)

      // Update local state
      setNormalizations(normalizations.map(n =>
        n.unique_serial_id === editingKpiId ? updatedNormalization : n
      ))

      // Exit edit mode
      setEditingKpiId(null)
      setInlineYaml("")
      setIntegrationValidationErrors([])
      setPendingSaveNormalization(null)

      toast.success(`Normalization for "${pendingSaveNormalization.kpi}" saved successfully`)
    } catch (error: any) {
      console.error('Failed to save YAML:', error)
      setInlineYamlError(error.message || 'Failed to save')
      toast.error('Failed to save normalization')
    } finally {
      setSavingInlineYaml(false)
    }
  }

  const _addRuleToKPI = (normalization: NormalizationType) => {
    // Set up state for adding a new rule to existing KPI
    setSelectedNormalization(normalization)
    setKpi(normalization.kpi)
    setTags(normalization.tags || "")
    setIsAddingRuleToExistingKPI(true)
    
    // Set the existing normalization config as YAML for editing
    const yamlConfig = convertConfigToYAML(normalization.normalization)
    setManualConfig(yamlConfig)
    
    // Convert to tokens for smart builder
    const tokens = yamlToTokens(yamlConfig)
    setRuleTokens(tokens)
    
    setShowEditDialog(true)
  }

  const _handleUpdateNormalizationWithNewRule = async () => {
    if (!selectedNormalization || !kpi.trim()) {
      toast.error("Please enter a KPI name")
      return
    }

    try {
      // Validate YAML syntax before proceeding
      const normalizationConfig = buildNormalizationConfig()

      // Extract dependencies from conditional normalizations
      const dependencies = extractConditionalDependencies(normalizationConfig)

      const updatedNormalization = await normalizationsService.update(
        selectedNormalization.unique_serial_id!,
        {
          kpi: kpi.trim(),
          normalization: normalizationConfig,
          dependencies: dependencies.length > 0 ? dependencies : undefined,
          tags: tags.trim() || undefined
        }
      )

      setNormalizations(normalizations.map(n => 
        n.unique_serial_id === selectedNormalization.unique_serial_id 
          ? updatedNormalization 
          : n
      ))
      setShowEditDialog(false)
      resetForm()
      toast.success("Rule added successfully")
    } catch (error: any) {
      console.error("Failed to add rule:", error)
      if (error.message === 'Invalid YAML syntax') {
        toast.error("Bad YAML syntax")
        setHasValidationError(true)
        // Keep dialog open so user can fix the syntax
        return
      }
      toast.error("Failed to add rule")
    }
  }

  // Helper function to format values for YAML output
  const formatYAMLValue = (value: any, isKey: boolean = false): string => {
    if (value === '' || value === null || value === undefined) {
      return '""'
    }
    if (typeof value === 'string') {
      // For keys: ALWAYS quote them with double quotes since they'll be stored as JSON
      // Double quotes allow single quotes inside (for conditionals like "integrator='google_ads'")
      if (isKey) {
        return `"${value}"`
      }

      // For values: quote if contains special YAML characters
      // or has leading/trailing whitespace
      if (value.match(/[:#@\[\]{}\|>]/) ||
          value.trim() === '' ||
          value !== value.trim()) {
        return `"${value}"`
      }
    }
    return String(value)
  }

  const convertConfigToYAML = (config: any): string => {
    if (!config || typeof config !== 'object') {
      return ''
    }

    const yamlLines: string[] = []

    Object.entries(config).forEach(([ruleType, ruleConfig]) => {
      yamlLines.push(`${ruleType}:`)

      if (typeof ruleConfig === 'string' || typeof ruleConfig === 'number' || typeof ruleConfig === 'boolean') {
        // Append simple values on the same line
        yamlLines[yamlLines.length - 1] += ` ${formatYAMLValue(ruleConfig)}`
      } else if (Array.isArray(ruleConfig)) {
        // Handle direct arrays (e.g., fuzzy rules stored as ["item1", "item2"])
        ruleConfig.forEach((item: any) => {
          const itemValue = typeof item === 'object' ? Object.values(item)[0] : item
          yamlLines.push(`  - ${itemValue}`)
        })
      } else if (typeof ruleConfig === 'object' && ruleConfig !== null) {
        // Handle different rule type structures
        const ruleConfigObj = ruleConfig as any
        if (ruleType === 'fuzzy' && ruleConfigObj.search_terms && Array.isArray(ruleConfigObj.search_terms)) {
          yamlLines.push('  search_terms:')
          ruleConfigObj.search_terms.forEach((term: any) => {
            const termValue = typeof term === 'object' ? Object.values(term)[0] : term
            yamlLines.push(`    - ${termValue}`)
          })
          if (ruleConfigObj.others !== undefined) {
            yamlLines.push(`  others: ${ruleConfigObj.others}`)
          }
        } else if (ruleConfigObj.replacements && typeof ruleConfigObj.replacements === 'object') {
          Object.entries(ruleConfigObj.replacements).forEach(([key, value]) => {
            yamlLines.push(`  ${formatYAMLValue(key, true)}: ${formatYAMLValue(value)}`)
          })
        } else {
          // Generic object handling
          const entries = Object.entries(ruleConfig)
          if (entries.length === 0) {
            // Empty rule object - add helpful placeholder
            yamlLines.push(`  # Add key-value pairs here (must be indented with 2 spaces)`)
            yamlLines.push(`  # Example: key: value`)
          } else {
            entries.forEach(([key, value]) => {
              if (key === 'search_terms' || key === 'thresholds') return // Skip already handled
              if (typeof value === 'object' && value !== null) {
                yamlLines.push(`  ${formatYAMLValue(key, true)}:`)
                Object.entries(value as any).forEach(([subKey, subValue]) => {
                  yamlLines.push(`    ${formatYAMLValue(subKey, true)}: ${formatYAMLValue(subValue)}`)
                })
              } else {
                yamlLines.push(`  ${formatYAMLValue(key, true)}: ${formatYAMLValue(value)}`)
              }
            })
          }
        }
      }
      
      yamlLines.push('') // Add blank line between rules
    })
    
    return yamlLines.join('\n').trim()
  }

  const formatRuleDetailsAsYAML = (_ruleType: string, ruleConfig: any): string => {
    // Format rule configuration as YAML-like text (without internal control terms)
    try {
      if (typeof ruleConfig === 'string' || typeof ruleConfig === 'number' || typeof ruleConfig === 'boolean') {
        return String(ruleConfig)
      }

      if (Array.isArray(ruleConfig)) {
        // Handle direct arrays (e.g., fuzzy rules)
        const lines: string[] = []
        ruleConfig.forEach((item: any) => {
          const itemValue = typeof item === 'object' ? Object.values(item)[0] : item
          lines.push(`  - ${itemValue}`)
        })
        return lines.join('\n')
      }

      if (typeof ruleConfig === 'object' && ruleConfig !== null) {
        // Handle specific rule types with internal control terms

        // For replace rules with replacements object
        if (ruleConfig.replacements && typeof ruleConfig.replacements === 'object') {
          const lines: string[] = []
          Object.entries(ruleConfig.replacements).forEach(([key, value]) => {
            lines.push(`  ${formatYAMLValue(key, true)}: ${formatYAMLValue(value)}`)
          })
          return lines.join('\n')
        }
        
        // For fuzzy matching with search_terms array
        if (ruleConfig.search_terms && Array.isArray(ruleConfig.search_terms)) {
          const lines: string[] = []
          
          // Add search terms - remove numbers for fuzzy matching type
          if (_ruleType === 'fuzzy') {
            ruleConfig.search_terms.forEach((term: any) => {
              const termValue = typeof term === 'object' ? Object.values(term)[0] : term
              lines.push(`${termValue}`)
            })
          } else {
            // Add search terms in proper YAML array format for other types
            lines.push('search_terms:')
            ruleConfig.search_terms.forEach((term: any) => {
              const termValue = typeof term === 'object' ? Object.values(term)[0] : term
              lines.push(`  - ${termValue}`)
            })
          }
          
          // Add others flag if present (but skip thresholds completely)
          if (ruleConfig.others !== undefined) {
            lines.push(`others: ${formatYAMLValue(ruleConfig.others)}`)
          }

          return lines.join('\n')
        }

        // For fuzzy matching with direct array structure
        if (_ruleType === 'fuzzy' && Array.isArray(ruleConfig)) {
          const lines: string[] = []
          ruleConfig.forEach((term: any) => {
            lines.push(`${term}`)
          })
          return lines.join('\n')
        }

        // Handle other configurations, but skip internal control terms
        const yamlLines: string[] = []
        const skipKeys = ['replacements', 'search_terms', 'thresholds', 'mappings', 'patterns', 'pairs', 'terms', 'conditions']

        Object.entries(ruleConfig).forEach(([key, value]) => {
          if (!skipKeys.includes(key)) {
            if (typeof value === 'object' && value !== null) {
              yamlLines.push(`${formatYAMLValue(key, true)}:`)
              Object.entries(value as any).forEach(([subKey, subValue]) => {
                yamlLines.push(`  ${formatYAMLValue(subKey, true)}: ${formatYAMLValue(subValue)}`)
              })
            } else {
              yamlLines.push(`  ${formatYAMLValue(key, true)}: ${formatYAMLValue(value)}`)
            }
          }
        })
        return yamlLines.join('\n')
      }
      
      return JSON.stringify(ruleConfig)
    } catch (error) {
      return '[Invalid format]'
    }
  }

  const getRuleEntries = (normalization: any): Array<{type: string, config: any}> => {
    if (!normalization || typeof normalization !== 'object') {
      return []
    }
    
    // Handle actual database format where rule types are direct properties
    // e.g., {lower: {...}}, {countries: 'ISO 3'}, {replace: {...}, partial_string: {...}, case: {...}}
    const entries: Array<{type: string, config: any}> = []
    
    Object.entries(normalization).forEach(([key, value]) => {
      // Filter out any non-rule properties if they exist
      if (key !== 'id' && key !== 'created_at' && key !== 'updated_at') {
        entries.push({ type: key, config: value })
      }
    })
    
    return entries
  }


  const _getBadgeVariant = (type: string): "default" | "secondary" | "outline" => {
    // Use different slate-based variants for visual distinction
    switch (type) {
      case 'upper':
      case 'lower':
      case 'title':
      case 'replace':
        return 'default'  // slate-900 background
      case 'partial_string':
      case 'countries':
      case 'mapping':
        return 'secondary'  // slate-100 background
      case 'currency':
      case 'fuzzy':
      case 'conditional':
      case 'string_match':
      default:
        return 'outline'  // slate border
    }
  }

  const filteredNormalizations = (normalizations || [])
    .sort((a, b) => a.kpi.localeCompare(b.kpi))

  // Extract primitive dependencies from conditional normalization
  const extractConditionalDependencies = (config: any): string[] => {
    const dependencies = new Set<string>()

    // Get list of actual primitive names from availableKPIs
    const primitiveNames = new Set(availableKPIs.map(kpi => kpi.name))

    // Helper to check if a string is a primitive reference
    const isPrimitiveReference = (value: string): boolean => {
      // Must match identifier pattern AND exist in our primitives list
      const primitivePattern = /^[a-zA-Z_][a-zA-Z0-9_]*$/
      return primitivePattern.test(value) && primitiveNames.has(value)
    }

    if (config.conditional && typeof config.conditional === 'object') {
      // Look at each condition expression (key) and its mapped value
      Object.entries(config.conditional).forEach(([condition, value]) => {
        if (condition.toLowerCase() === 'default') return

        // Extract column references from condition expression
        const columnPattern = /\b([a-zA-Z_][a-zA-Z0-9_]*)\s*(?:={1,2}|!=|>=|<=|>|<|contains|in)/gi
        let match
        while ((match = columnPattern.exec(condition)) !== null) {
          const potentialColumn = match[1]
          // Skip SQL/logical keywords
          if (!['and', 'or', 'not', 'true', 'false', 'null'].includes(potentialColumn.toLowerCase())) {
            // Only add if it's an actual primitive
            if (primitiveNames.has(potentialColumn)) {
              dependencies.add(potentialColumn)
            }
          }
        }

        // Also check if the mapped value is a primitive reference
        if (typeof value === 'string' && isPrimitiveReference(value)) {
          dependencies.add(value)
        }
      })

      // Also check the default value - if it references a primitive, add it
      const defaultValue = config.conditional.default
      if (defaultValue && typeof defaultValue === 'string' && isPrimitiveReference(defaultValue)) {
        dependencies.add(defaultValue)
      }
    }

    return Array.from(dependencies)
  }

  // Validate integration conditionals (e.g., integration = 'google_ads#123')
  // Returns array of validation errors, empty if all valid
  const validateIntegrationConditionalsInConfig = (config: any): IntegrationValidationError[] => {
    const allReferences: string[] = []

    console.log('🔍 Validating config:', JSON.stringify(config, null, 2))
    console.log('🔍 Has conditional?', !!config.conditional, typeof config.conditional)

    if (config.conditional && typeof config.conditional === 'object') {
      const conditions = Object.keys(config.conditional)
      console.log('🔍 Conditional keys:', conditions)

      // Look at each condition expression (key)
      conditions.forEach((condition) => {
        if (condition.toLowerCase() === 'default') return

        // Extract integration references from this condition
        const refs = extractIntegrationReferences(condition)
        console.log(`🔍 Condition "${condition}" -> refs:`, refs)
        allReferences.push(...refs)
      })
    }

    if (allReferences.length === 0) {
      console.log('⚠️ No integration references found')
      return []
    }

    console.log('🔍 All integration references:', allReferences)

    // Always validate format and existence (if data loaded)
    // Format validation works even without lookup data
    const validationDataLoaded = validIntegrators.size > 0 || validIntegrations.size > 0
    if (!validationDataLoaded) {
      console.log('⚠️ Validation data not loaded - only format validation will run')
    }

    // Validate all references
    const errors = validateIntegrationReferences(
      allReferences,
      validIntegrators,
      validIntegrations
    )

    if (errors.length > 0) {
      console.log('❌ Integration validation errors:', errors)
    }

    return errors
  }

  const parseYAMLConfig = (yamlText: string): any => {
    const config: any = {}
    const lines = yamlText.split('\n').map(line => line.trimEnd())

    let currentRule: string | null = null
    let currentArray: string[] = []
    let inArray = false
    let lineNumber = 0

    for (const line of lines) {
      lineNumber++
      if (!line.trim()) continue
      
      // Check for rule definition (no leading spaces)
      if (line.match(/^[a-zA-Z_][a-zA-Z0-9_]*:$/)) {
        // Save previous array if exists
        if (currentRule && inArray && currentArray.length > 0) {
          if (currentRule === 'fuzzy') {
            config[currentRule] = currentArray
          } else {
            if (!config[currentRule]) config[currentRule] = {}
            config[currentRule].search_terms = currentArray
          }
        }
        
        currentRule = line.replace(':', '')
        config[currentRule] = {}
        currentArray = []
        inArray = false
        continue
      }
      
      if (!currentRule) {
        throw new Error(`Invalid YAML: Content at line ${lineNumber} must be inside a rule block. Start with a rule name like 'fuzzy:'`)
      }
      
      // Check for array items (lines starting with spaces and dash: "  - " or "-" or " -")
      const arrayMatch = line.match(/^(\s*)(-\s*)(.*)/)
      if (arrayMatch) {
        if (!inArray) {
          inArray = true
          currentArray = []
        }
        const arrayValue = arrayMatch[3].trim()
        if (!arrayValue) {
          throw new Error(`Invalid YAML: Empty array item at line ${lineNumber}. Example: '  - Contract Tracker'`)
        }
        currentArray.push(arrayValue)
        continue
      }
      
      // If we were in an array and now we're not, save the array
      if (inArray && !line.match(/^\s*-\s*/)) {
        if (currentRule === 'fuzzy') {
          config[currentRule] = currentArray
        } else {
          config[currentRule].search_terms = currentArray
        }
        inArray = false
        currentArray = []
      }
      
      // Check for key-value pairs (lines with indentation and colon)
      // Match both quoted keys ("key") and unquoted keys (key)
      const keyValueMatch = line.match(/^(\s*)((?:"[^"]*")|(?:'[^']*')|(?:[a-zA-Z0-9_.]+)):\s*(.*)/)
      if (keyValueMatch && keyValueMatch[1].length > 0) { // Must have indentation
        let key = keyValueMatch[2]
        let value = keyValueMatch[3]

        // Remove surrounding quotes from key if present
        if ((key.startsWith('"') && key.endsWith('"')) || (key.startsWith("'") && key.endsWith("'"))) {
          key = key.slice(1, -1)
        }

        // Remove surrounding quotes from value if present
        if ((value.startsWith('"') && value.endsWith('"')) || (value.startsWith("'") && value.endsWith("'"))) {
          value = value.slice(1, -1)
        }

        if (!key) {
          throw new Error(`Invalid YAML: Empty key at line ${lineNumber}`)
        }

        // Store key-value pairs directly in the rule config (flat structure)
        config[currentRule][key] = value === 'true' ? true : value === 'false' ? false : value
        continue
      }
      
      // Check for invalid indentation
      if (line.match(/^ /)) {
        if (!line.match(/^  /)) {
          throw new Error(`Invalid YAML: Incorrect indentation at line ${lineNumber}. Use exactly 2 spaces.`)
        }
        if (line.match(/^   /)) {
          throw new Error(`Invalid YAML: Too much indentation at line ${lineNumber}. Use exactly 2 spaces.`)
        }
      }
      
      // Handle simple values (indented content that's not an array or key-value)
      const simpleValueMatch = line.match(/^(\s+)(.+)/)
      if (simpleValueMatch && simpleValueMatch[1].length > 0) {
        const value = simpleValueMatch[2].trim()
        if (!value) {
          throw new Error(`Invalid YAML: Empty value at line ${lineNumber}`)
        }
        if (Object.keys(config[currentRule]).length === 0) {
          config[currentRule] = value
        }
      } else {
        // If line doesn't match any pattern, it's invalid
        throw new Error(`Invalid YAML: Unrecognized syntax at line ${lineNumber}: "${line}".\n\n` +
          `Key-value pairs MUST be indented with exactly 2 spaces under rule '${currentRule}'.\n\n` +
          `Examples:\n` +
          `  ${currentRule}:\n` +
          `    key: value\n` +
          `    another_key: "another value"\n\n` +
          `Or for arrays:\n` +
          `  ${currentRule}:\n` +
          `    - item1\n` +
          `    - item2`)
      }
    }
    
    // Save final array if exists
    if (currentRule && inArray && currentArray.length > 0) {
      if (currentRule === 'fuzzy') {
        config[currentRule] = currentArray
      } else {
        if (!config[currentRule]) config[currentRule] = {}
        config[currentRule].search_terms = currentArray
      }
    }
    
    // Validate that we have at least one rule
    if (Object.keys(config).length === 0) {
      throw new Error('Invalid YAML: No rules defined')
    }
    
    return config
  }

  // Convert tokens to YAML configuration
  const tokensToYAML = (tokens: any[]): string => {
    const config: any = {}
    
    tokens.forEach(token => {
      if (token.type === 'replace') {
        if (!config.replace) config.replace = {}
        config.replace[token.from] = token.to
      } else if (token.type === 'fuzzy') {
        if (!config.fuzzy) config.fuzzy = []
        if (token.terms) {
          config.fuzzy = [...(config.fuzzy || []), ...token.terms]
        }
      } else if (token.type === 'countries') {
        if (!config.countries) config.countries = {}
        if (token.output) config.countries.output = token.output
        if (token.others !== undefined) config.countries.others = token.others
      } else if (token.type === 'mapping') {
        if (!config.mapping) config.mapping = { replacements: {} }
        if (token.mappings) {
          Object.assign(config.mapping.replacements, token.mappings)
        }
      }
    })
    
    return convertConfigToYAML(config)
  }
  
  // Convert YAML config to tokens
  const yamlToTokens = (yamlText: string): any[] => {
    if (!yamlText.trim()) return []
    
    try {
      const config = parseYAMLConfig(yamlText)
      const tokens: any[] = []
      
      Object.entries(config || {}).forEach(([ruleType, ruleConfig]) => {
        if (ruleType === 'replace' && typeof ruleConfig === 'object' && ruleConfig) {
          Object.entries(ruleConfig).forEach(([from, to]) => {
            tokens.push({ 
              id: Date.now() + Math.random(), 
              type: 'replace', 
              from, 
              to, 
              display: `Replace "${from}" with "${to}"` 
            })
          })
        } else if (ruleType === 'fuzzy' && Array.isArray(ruleConfig)) {
          tokens.push({
            id: Date.now() + Math.random(),
            type: 'fuzzy',
            terms: ruleConfig,
            threshold: 0.8,
            display: `Fuzzy match: ${ruleConfig.join(', ')} (0.8)`
          })
        } else if (ruleType === 'countries' && typeof ruleConfig === 'object') {
          const configObj = ruleConfig as any
          tokens.push({
            id: Date.now() + Math.random(),
            type: 'countries',
            output: configObj.output,
            others: configObj.others,
            display: `Countries: output=${configObj.output}, others=${configObj.others}`
          })
        }
      })
      
      return tokens
    } catch (error) {
      console.error('Failed to convert YAML to tokens:', error)
      return []
    }
  }
  
  // Parse natural language input into tokens
  const parseTokenInput = (input: string): any | null => {
    const lowerInput = input.toLowerCase().trim()
    
    // Replace patterns
    const replaceMatch = lowerInput.match(/replace\s+["']?([^"']+)["']?\s+with\s+["']?([^"']+)["']?/)
    if (replaceMatch) {
      return {
        id: Date.now() + Math.random(),
        type: 'replace',
        from: replaceMatch[1].trim(),
        to: replaceMatch[2].trim(),
        display: `Replace "${replaceMatch[1].trim()}" with "${replaceMatch[2].trim()}"`
      }
    }
    
    // Fuzzy patterns
    const fuzzyMatch = lowerInput.match(/(?:fuzzy\s+match|find)\s+["']?([^"']+)["']?(?:\s+(?:with\s+)?(?:threshold|accuracy)\s+(\d*\.?\d+))?/)
    if (fuzzyMatch) {
      const terms = fuzzyMatch[1].split(',').map(t => t.trim())
      const threshold = fuzzyMatch[2] ? parseFloat(fuzzyMatch[2]) : 0.8
      return {
        id: Date.now() + Math.random(),
        type: 'fuzzy',
        terms,
        threshold,
        display: `Fuzzy match: ${terms.join(', ')} (threshold: ${threshold})`
      }
    }
    
    // Countries patterns
    const countriesMatch = lowerInput.match(/countries?\s+output\s+(?:to\s+)?["']?([^"']+)["']?(?:\s+others\s+(true|false))?/)
    if (countriesMatch) {
      return {
        id: Date.now() + Math.random(),
        type: 'countries',
        output: countriesMatch[1].trim(),
        others: countriesMatch[2] ? countriesMatch[2] === 'true' : true,
        display: `Countries: output=${countriesMatch[1].trim()}, others=${countriesMatch[2] || 'true'}`
      }
    }
    
    return null
  }
  
  // Generate suggestions based on input
  const generateSuggestions = (input: string): string[] => {
    const lowerInput = input.toLowerCase()
    const suggestions: string[] = []
    
    if (lowerInput.includes('replace') || lowerInput.includes('change')) {
      suggestions.push('Replace "old_value" with "new_value"')
      suggestions.push('Replace "875163" with "Capricho"')
    }
    
    if (lowerInput.includes('fuzzy') || lowerInput.includes('find') || lowerInput.includes('search')) {
      suggestions.push('Fuzzy match "Contract Tracker" with threshold 0.8')
      suggestions.push('Find "Service Agreement, Contract, Agreement"')
    }
    
    if (lowerInput.includes('country') || lowerInput.includes('countries')) {
      suggestions.push('Countries output to "name" others true')
      suggestions.push('Countries output to "code" others false')
    }
    
    if (lowerInput.length > 0 && suggestions.length === 0) {
      suggestions.push(
        'Replace "old_value" with "new_value"',
        'Fuzzy match "search_term"',
        'Countries output to "name"'
      )
    }
    
    return suggestions
  }
  
  // Handle token input
  const _handleTokenInput = (value: string) => {
    setTokenInput(value)
    
    if (value.trim()) {
      const newSuggestions = generateSuggestions(value)
      setSuggestions(newSuggestions)
      setShowSuggestions(true)
    } else {
      setShowSuggestions(false)
    }
  }
  
  // Add token from input
  const _addToken = (input?: string) => {
    const textToProcess = input || tokenInput
    if (!textToProcess.trim()) return
    
    const token = parseTokenInput(textToProcess)
    if (token) {
      setRuleTokens([...ruleTokens, token])
      setTokenInput('')
      setShowSuggestions(false)
      // Update YAML preview
      const newYaml = tokensToYAML([...ruleTokens, token])
      setManualConfig(newYaml)
    } else {
      toast.error('Could not understand that input. Try: "Replace X with Y" or "Fuzzy match term"')
    }
  }
  
  // Remove token
  const _removeToken = (tokenId: string | number) => {
    const newTokens = ruleTokens.filter(token => token.id !== tokenId)
    setRuleTokens(newTokens)
    // Update YAML preview
    const newYaml = tokensToYAML(newTokens)
    setManualConfig(newYaml)
  }
  
  // Switch between token builder and YAML editor
  const _switchToTokenBuilder = () => {
    // Convert current YAML to tokens
    if (manualConfig.trim()) {
      const tokens = yamlToTokens(manualConfig)
      setRuleTokens(tokens)
    }
    setUseTokenBuilder(true)
  }
  
  const _switchToYAMLEditor = () => {
    // Convert current tokens to YAML
    if (ruleTokens.length > 0) {
      const yaml = tokensToYAML(ruleTokens)
      setManualConfig(yaml)
    }
    setUseTokenBuilder(false)
  }
  
  const buildNormalizationConfig = () => {
    try {
      if (useTokenBuilder) {
        // Build from tokens
        const yamlFromTokens = tokensToYAML(ruleTokens)
        if (yamlFromTokens.trim()) {
          return parseYAMLConfig(yamlFromTokens)
        }
        return {}
      } else {
        // Use manual YAML
        if (manualConfig.trim()) {
          return parseYAMLConfig(manualConfig)
        }
        return {}
      }
    } catch (error) {
      console.error('Failed to parse configuration:', error)
      throw new Error('Invalid configuration')
    }
  }

  // Legacy function - no longer used with YAML approach
  // @ts-ignore - Function kept for reference but not used
  const _renderRuleConfig = (rule: any, index: number) => {
    const updateRuleConfig = (configUpdate: any) => {
      // Legacy function - not used anymore
      console.log('Legacy function called:', configUpdate)
    }

    switch (rule.type) {
      case 'mapping':
        return (
          <div className="space-y-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Value Mappings</label>
              <Textarea
                placeholder="Enter value mappings, one per line:&#10;original_value1 -> mapped_value1&#10;original_value2 -> mapped_value2"
                value={rule.config.mappings || ''}
                onChange={(e) => updateRuleConfig({ mappings: e.target.value })}
                rows={4}
                className="font-mono text-sm"
              />
            </div>
          </div>
        )

      case 'string_match':
        return (
          <div className="space-y-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Pattern Matches</label>
              <Textarea
                placeholder="Enter pattern matches, one per line:&#10;pattern1 -> replacement1&#10;pattern2 -> replacement2"
                value={rule.config.patterns || ''}
                onChange={(e) => updateRuleConfig({ patterns: e.target.value })}
                rows={4}
                className="font-mono text-sm"
              />
            </div>
          </div>
        )

      case 'replace':
        return (
          <div className="space-y-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Find & Replace Pairs</label>
              <Textarea
                placeholder="Enter search and replace pairs, one per line:&#10;old_value1 -> new_value1&#10;old_value2 -> new_value2"
                value={rule.config.pairs || ''}
                onChange={(e) => updateRuleConfig({ pairs: e.target.value })}
                rows={4}
                className="font-mono text-sm"
              />
            </div>
          </div>
        )

      case 'countries':
        return (
          <div className="space-y-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Country Format</label>
              <Select
                value={rule.config.format || rule.config.value || rule.config || ''}
                onValueChange={(value) => updateRuleConfig({ format: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select country format..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ISO 2">
                    <div className="flex flex-col">
                      <span>ISO 2 Code</span>
                      <span className="text-xs text-muted-foreground">Two letter codes (US, CA)</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="ISO 3">
                    <div className="flex flex-col">
                      <span>ISO 3 Code</span>
                      <span className="text-xs text-muted-foreground">Three letter codes (USA, CAN)</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="full_name">
                    <div className="flex flex-col">
                      <span>Full Name</span>
                      <span className="text-xs text-muted-foreground">Complete country names</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )

      case 'currency':
        return (
          <div className="space-y-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Target Currency</label>
              <Input
                placeholder="e.g., USD, EUR, GBP"
                value={rule.config.currency || rule.config.value || rule.config || ''}
                onChange={(e) => updateRuleConfig({ currency: e.target.value.toUpperCase() })}
              />
            </div>
          </div>
        )

      case 'fuzzy':
        return (
          <div className="space-y-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Match Terms (one per line)</label>
              <Textarea
                placeholder="Enter search terms, one per line:&#10;term1&#10;term2&#10;others"
                value={rule.config.terms || ''}
                onChange={(e) => updateRuleConfig({ terms: e.target.value })}
                rows={4}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Similarity Threshold</label>
              <Input
                type="number"
                min="0"
                max="100"
                placeholder="80"
                value={rule.config.threshold || ''}
                onChange={(e) => updateRuleConfig({ threshold: parseInt(e.target.value) || 80 })}
              />
            </div>
          </div>
        )

      case 'conditional':
        return (
          <div className="space-y-3">
            <div className="space-y-2">
              <label className="text-sm font-medium">Conditional Logic</label>
              <Textarea
                placeholder="Enter conditions and values:&#10;(condition1) value1&#10;(condition2) value2&#10;default default_value"
                value={rule.config.conditions || ''}
                onChange={(e) => updateRuleConfig({ conditions: e.target.value })}
                rows={4}
                className="font-mono text-sm"
              />
            </div>
          </div>
        )

      case 'upper':
      case 'lower':
      case 'title':
        return (
          <div className="text-sm text-gray-600">
            This rule will convert text to {rule.type} case. No additional configuration needed.
          </div>
        )

      default:
        return (
          <div className="space-y-2">
            <label className="text-sm font-medium">Configuration (JSON)</label>
            <Textarea
              placeholder='{"key": "value"}'
              value={JSON.stringify(rule.config || {}, null, 2)}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value)
                  updateRuleConfig(parsed)
                } catch {
                  // Invalid JSON, don't update
                }
              }}
              rows={3}
              className="font-mono text-sm"
            />
          </div>
        )
    }
  }

  // Suppress unused variable warnings for unimplemented features
  void _handleEditNormalization
  void _handleGenerateWithAI
  void _openRuleEditDialog
  void _openRuleDeleteDialog
  void _duplicateRule
  void _addRuleToKPI
  void _handleUpdateNormalizationWithNewRule
  void _getBadgeVariant
  void _handleTokenInput
  void _addToken
  void _removeToken
  void _switchToTokenBuilder
  void _switchToYAMLEditor

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight text-slate-900">Normalizations</h2>
          <p className="text-slate-600">
            Create and manage data normalization rules for your KPIs
          </p>
        </div>
        <Button onClick={() => setShowNewDialog(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New
        </Button>
      </div>

      <div className="rounded-md border overflow-hidden">
        <div className="overflow-x-auto">
        <Table className="min-w-[700px]">
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[100px]">KPI</TableHead>
              <TableHead className="min-w-[200px]">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help">Rules</span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Rules are in YAML format. Click to edit.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </TableHead>
              <TableHead className="w-[100px] text-center">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="cursor-help">Anonymize</span>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>This will anonymize this KPI when sending data to Agents</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                  Loading normalizations...
                </TableCell>
              </TableRow>
            ) : filteredNormalizations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-8 text-slate-500">
                  No normalizations created yet
                </TableCell>
              </TableRow>
            ) : (
              filteredNormalizations.map((normalization) => {
                const isEditing = editingKpiId === normalization.unique_serial_id
                const yamlContent = convertConfigToYAML(normalization.normalization)

                return (
                  <TableRow key={normalization.unique_serial_id}>
                    <TableCell className="font-medium align-top">
                      {normalization.kpi}
                    </TableCell>
                    <TableCell className="align-top">
                      {isEditing ? (
                        <div className="space-y-2">
                          <YamlEditor
                            value={inlineYaml}
                            onChange={setInlineYaml}
                            errorLines={errorLineNumbers}
                            placeholder="Enter YAML rules configuration..."
                            minHeight="200px"
                            className="text-xs"
                          />
                          {inlineYamlError && (
                            <div className="text-xs text-red-600 bg-red-50 p-2 rounded border border-red-200">
                              {inlineYamlError}
                            </div>
                          )}
                          {integrationValidationErrors.length > 0 && (
                            <div className="text-xs text-amber-700 bg-amber-50 p-2 rounded border border-amber-200 space-y-1">
                              <div className="font-medium">Integration validation errors:</div>
                              {integrationValidationErrors.map((err, idx) => (
                                <div key={idx}>
                                  <span className="font-mono">'{err.value}'</span>: {err.error}
                                  {err.suggestion && <span className="text-amber-600 ml-1">{err.suggestion}</span>}
                                </div>
                              ))}
                            </div>
                          )}
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={() => validateAndConfirmSave(normalization)}
                              disabled={savingInlineYaml}
                            >
                              {savingInlineYaml ? "Saving..." : "Save"}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={cancelInlineEdit}
                              disabled={savingInlineYaml}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <pre
                          className="font-mono text-xs whitespace-pre-wrap cursor-pointer hover:bg-slate-50 p-2 -m-2 rounded transition-colors"
                          onClick={() => enterInlineEditMode(normalization)}
                          title="Click to edit rules"
                        >
                          {yamlContent || <span className="text-slate-400">No rules defined</span>}
                        </pre>
                      )}
                    </TableCell>
                    <TableCell className="align-top">
                      <div className="flex items-center justify-center h-8">
                        <Switch
                          checked={normalization.anonymize ?? false}
                          onCheckedChange={async (checked) => {
                            const id = normalization.unique_serial_id
                            try {
                              await normalizationsService.update(id!, { anonymize: checked })
                              setNormalizations(prev => prev.map(n =>
                                n.unique_serial_id === id ? { ...n, anonymize: checked } : n
                              ))
                              toast.success(`Anonymize ${checked ? 'enabled' : 'disabled'} for ${normalization.kpi}`)
                            } catch (error) {
                              console.error('Failed to update anonymize:', error)
                              toast.error('Failed to update anonymize setting')
                            }
                          }}
                        />
                      </div>
                    </TableCell>
                    <TableCell className="align-top">
                      <div className="flex items-center justify-center h-8">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                          onClick={() => openDeleteDialog(normalization)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
        </div>
      </div>

      {/* Create Dialog - Simple KPI Selector */}
      <Dialog open={showNewDialog} onOpenChange={(open) => {
        if (!open) {
          setShowNewDialog(false)
          resetForm()
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Normalization</DialogTitle>
            <DialogDescription>
              Select a KPI to add normalization rules for. You'll be able to edit the rules inline after creation.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">KPI Name</label>
              <Select
                value={kpi}
                onValueChange={setKpi}
                disabled={loadingKPIs}
              >
                <SelectTrigger>
                  <SelectValue placeholder={loadingKPIs ? "Loading KPIs..." : "Select a KPI..."} />
                </SelectTrigger>
                <SelectContent>
                  {availableKPIs
                    .filter((kpi_option) => {
                      // Filter out KPIs that are already used in normalizations
                      const usedKPIs = normalizations.map(n => n.kpi)
                      return !usedKPIs.includes(kpi_option.slug || kpi_option.name)
                    })
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((kpi_option) => (
                      <SelectItem key={kpi_option.slug || kpi_option.name} value={kpi_option.slug || kpi_option.name}>
                        {kpi_option.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Select a KPI to add normalization rules for. You'll be able to edit the rules inline after creation.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowNewDialog(false)
                resetForm()
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateNormalization}
              disabled={!kpi}
            >
              Add KPI
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Enhanced KPI Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={(open) => {
        setShowDeleteDialog(open)
        if (!open) setDeleteConfirmation("")
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>⚠️ Delete Entire Normalization</AlertDialogTitle>
            <AlertDialogDescription>
              Delete ALL normalization rules for "{selectedNormalization?.kpi}"?
              <br /><br />
              This will permanently remove all data transformation rules for this KPI.
              This action cannot be undone and affects the entire platform.
              <br /><br />
              Type "DELETE" to confirm:
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-6">
            <Input
              value={deleteConfirmation}
              onChange={(e) => setDeleteConfirmation(e.target.value)}
              placeholder="Type DELETE to confirm"
              className="font-mono"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteConfirmation("")}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteNormalization} 
              disabled={deleteConfirmation !== "DELETE"}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
            >
              Delete Normalization
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Individual Rule Edit Dialog */}
      <Dialog open={showRuleEditDialog} onOpenChange={(open) => {
        if (!open) {
          setShowRuleEditDialog(false)
          setEditingRule(null)
          setEditingRuleYAML("")
        }
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit {editingRule?.ruleType} Rule</DialogTitle>
            <DialogDescription>
              Edit the {editingRule?.ruleType} rule for "{editingRule?.normalization.kpi}". 
              Changes will affect how this KPI's data is transformed across the platform.
            </DialogDescription>
          </DialogHeader>

          {editingRule && (
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Rule Configuration</label>
                <Textarea
                  value={editingRuleYAML}
                  onChange={(e) => setEditingRuleYAML(e.target.value)}
                  placeholder="Enter rule configuration in YAML format..."
                  rows={12}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground">
                  Edit the rule configuration directly. Changes are saved in the same format as displayed in the table.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowRuleEditDialog(false)
                setEditingRule(null)
                setEditingRuleYAML("")
              }}
            >
              Cancel
            </Button>
            <Button onClick={async () => {
              if (!editingRule) return
              
              try {
                // Parse the edited YAML to get the new rule config
                let newRuleConfig: any
                
                if (editingRule.ruleType === 'fuzzy') {
                  // For fuzzy matching, parse as simple array (one item per line)
                  const lines = editingRuleYAML.trim().split('\n').map(line => line.trim()).filter(line => line)
                  newRuleConfig = lines
                } else {
                  // For other rule types, use the YAML parser
                  const parsed = parseYAMLConfig(`${editingRule.ruleType}:\n${editingRuleYAML}`)
                  newRuleConfig = parsed[editingRule.ruleType]
                }

                // Create updated normalization field (not the whole object)
                const updatedNormalizationField: Record<string, any> = { ...editingRule.normalization.normalization }

                // Update the specific rule in the normalization field
                updatedNormalizationField[editingRule.ruleType] = newRuleConfig

                // Save to database
                const savedNormalization = await normalizationsService.update(
                  editingRule.normalization.unique_serial_id!,
                  {
                    kpi: editingRule.normalization.kpi,
                    normalization: updatedNormalizationField,
                    tags: editingRule.normalization.tags
                  }
                )

                // Update local state
                setNormalizations(normalizations.map(n => 
                  n.unique_serial_id === editingRule.normalization.unique_serial_id 
                    ? savedNormalization 
                    : n
                ))

                setShowRuleEditDialog(false)
                setEditingRule(null)
                setEditingRuleYAML("")
              } catch (error: any) {
                console.error("Failed to update rule:", error)
                toast.error("Failed to update rule: " + (error.message || "Unknown error"))
              }
            }}>
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Individual Rule Delete Confirmation Dialog */}
      <AlertDialog open={showRuleDeleteDialog} onOpenChange={setShowRuleDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>⚠️ Delete Normalization Rule</AlertDialogTitle>
            <AlertDialogDescription>
              Delete this {deletingRule?.ruleType} rule for "{deletingRule?.normalization.kpi}"?
              <br /><br />
              This will affect how {deletingRule?.normalization.kpi} data is transformed across the platform.
              All future data collection will use the updated normalization rules.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                // TODO: Implement individual rule deletion
                toast.success('Rule deletion functionality coming soon')
                setShowRuleDeleteDialog(false)
                setDeletingRule(null)
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Rule
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Save Confirmation Dialog */}
      <AlertDialog open={showSaveConfirmDialog} onOpenChange={(open) => {
        setShowSaveConfirmDialog(open)
        if (!open) setPendingSaveNormalization(null)
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Save Normalization Changes</AlertDialogTitle>
            <AlertDialogDescription>
              Save changes to normalization rules for "{pendingSaveNormalization?.kpi}"?
              <br /><br />
              This will affect how data is transformed across the platform.
              All future data processing will use the updated normalization rules.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={saveInlineYaml}
              disabled={savingInlineYaml}
            >
              {savingInlineYaml ? "Saving..." : "Save Changes"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}